import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { ProviderField } from '../../../models/provider';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Button } from '../../atoms/Button/Button';
import { Footer } from '../../atoms/Footer/Footer';
import {
  ProviderInputs,
  SubmitProviderInputsPayload,
} from '../../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { TemplateElementStyle, TemplateElementType, Templates } from '../templates';
import { H1 } from '../../atoms/Heading/Heading';
import { Text } from '../../atoms/Text/Text';
import { MaybeChangeMethodButton } from '../MaybeChangeMethodButton/MaybeChangeMethodButton';
import { commonMessages } from '../../atoms/CommonMessages/messages';
import { messages } from './messages';

export type VerificationTemplateViewProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
};

export const VerificationTemplateView = ({ fields, onSubmit }: VerificationTemplateViewProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  const parsedElements = useMemo(() => {
    const title = fields.find(
      (el: ProviderField) =>
        el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );
    const description = fields.find(
      (el: ProviderField) =>
        el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.INSTRUCTION
    );
    const inputs = fields.filter((el) => el.type === TemplateElementType.INPUT);

    return { title, description, inputs };
  }, [fields]);

  const { title, description, inputs } = parsedElements;

  return (
    <ContentBody data-testid="verification-template-screen">
      <H1 mb="xxsmall">{title?.value ? title.value : <FormattedMessage {...messages.title} />}</H1>

      <Text color="secondary" mb="small">
        {description?.value && description?.helpText ? (
          <>
            {description.value} <strong>{description.helpText}</strong>
          </>
        ) : (
          <FormattedMessage {...messages.subtitle} />
        )}
      </Text>
      <Spacer size="xsmall" />

      <ProviderInputs
        formId={Templates.VERIFICATION}
        providerFields={inputs}
        setButtonDisabled={(newState) => setIsButtonDisabled(newState)}
        onSubmit={onSubmit}
      />

      <Spacer size="normal" />

      <Footer stickToBottom px="none">
        <Button
          form={Templates.VERIFICATION}
          disabled={isButtonDisabled}
          type="submit"
          color="primary"
          variant="contained"
        >
          <FormattedMessage {...commonMessages.continue} />
        </Button>
        <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
      </Footer>
    </ContentBody>
  );
};
